import React, { Fragment } from 'react';
import { ApplicationState, AppState } from '../../../store';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider
} from '@material-ui/core';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import {
  OrganizationBranding,
  SaveOrganizationBrandingResponse
} from '../../../interfaces/Organization';
import { IOrganizationService } from '../../../services/Interfaces/IOrganizationService';
import { withSnackbar, ProviderContext } from 'notistack';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import { ScaleLoader } from 'react-spinners';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  BRANDING_PREVIEW_PARTS,
  BRANDING_LOGO_ACCEPTED_TYPES,
  BRANDING_LOGO_MAX_SIZE_BYTES,
  BRANDING_LOGO_MIN_WIDTH,
  BRANDING_LOGO_MIN_HEIGHT
} from '../../../helpers/Constants';

interface IBrandingDocumentState {
  organizationBranding: OrganizationBranding;
  logoDataUrl: string | null;
  isLoading: boolean;
  isSaving: boolean;
}

type IBrandingDocumentProps = { appState: AppState } & ProviderContext &
  RouteComponentProps<{ id: string }>;

class OrganizationBrandingDocument extends React.PureComponent<
  IBrandingDocumentProps,
  IBrandingDocumentState
> {
  private translatorService!: ITranslatorService;
  private organizationService!: IOrganizationService;
  private fileInputRef = React.createRef<HTMLInputElement>();
  private previewRef = React.createRef<HTMLDivElement>();

  static contextType = ApplicationContext;

  private get organizationId(): number {
    return Number.parseInt(this.props.match.params.id);
  }

  private emptyBranding = (): OrganizationBranding => ({
    id: 0,
    organizationId: this.organizationId,
    title: '',
    headerDetails: '',
    footerDetails: '',
    additionalNotes: '',
    dateCreation: '',
    dateModification: '',
    createdBy: '',
    modifiedBy: ''
  });

  state = {
    organizationBranding: this.emptyBranding(),
    logoDataUrl: null,
    isLoading: true,
    isSaving: false
  } as IBrandingDocumentState;

  public componentDidMount() {
    this.setState({ isLoading: true }, async () => {
      await this.loadData();
    });
  }

  private static blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  private static getImageSize = (dataUrl: string): Promise<{ width: number; height: number }> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = reject;
      img.src = dataUrl;
    });

  loadData = async () => {
    try {
      const [branding, logo] = await Promise.all([
        this.organizationService.GetOrganizationBranding(this.organizationId),
        this.organizationService.GetLogo(this.organizationId).catch(() => null)
      ]);

      const logoDataUrl =
        logo && (logo as Blob).size > 0
          ? await OrganizationBrandingDocument.blobToDataUrl(logo as Blob)
          : null;

      this.setState({ organizationBranding: branding, logoDataUrl, isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  handleFieldChange = (field: keyof OrganizationBranding) => (event: any) => {
    this.setState({
      organizationBranding: { ...this.state.organizationBranding, [field]: event.target.value }
    });
  };

  triggerLogoUpload = () => {
    this.fileInputRef.current?.click();
  };

  handleLogoSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    event.target.value = '';

    if (!BRANDING_LOGO_ACCEPTED_TYPES.includes(file.type)) {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate(
          'ORGANIZATION_BRANDING_LOGO_FORMAT',
          'Format logo invalid (doar PNG sau JPG).'
        ),
        { variant: 'error' }
      );
      return;
    }

    if (file.size > BRANDING_LOGO_MAX_SIZE_BYTES) {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate(
          'ORGANIZATION_BRANDING_LOGO_MAX_SIZE',
          'Logo prea mare (maxim 2 MB).'
        ),
        { variant: 'error' }
      );
      return;
    }

    const logoDataUrl = await OrganizationBrandingDocument.blobToDataUrl(file);
    const size = await OrganizationBrandingDocument.getImageSize(logoDataUrl);
    if (size.width < BRANDING_LOGO_MIN_WIDTH || size.height < BRANDING_LOGO_MIN_HEIGHT) {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate(
          'ORGANIZATION_BRANDING_LOGO_MIN_RESOLUTION',
          'Rezoluție logo sub minimul recomandat (300×100 px).'
        ),
        { variant: 'warning' }
      );
    }

    try {
      await this.organizationService.AddLogo(this.organizationId, file);
      this.setState({ logoDataUrl });
      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  saveBranding = async () => {
    const request: OrganizationBranding = {
      ...this.state.organizationBranding,
      organizationId: this.organizationId
    };

    try {
      this.setState({ isSaving: true });
      const response: SaveOrganizationBrandingResponse =
        await this.organizationService.SaveOrganizationBranding(request);

      if (!response.success) {
        this.setState({ isSaving: false });
        this.props.enqueueSnackbar(
          this.translatorService.Tranlate(response.message, response.message),
          { variant: 'error' }
        );
        return;
      }

      this.setState({ organizationBranding: response.organizationBranding, isSaving: false });
      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.setState({ isSaving: false });
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  downloadModelPdf = async () => {
    const node = this.previewRef.current;
    if (!node) {
      return;
    }

    try {
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: '#ffffff' });
      const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const margin = 10;
      const width = doc.internal.pageSize.getWidth() - margin * 2;
      const height = (canvas.height / canvas.width) * width;
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin, width, height);
      doc.save(
        this.translatorService.Tranlate('ORGANIZATION_BRANDING_TEST_PDF_FILENAME', 'deviz-test.pdf')
      );
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  private renderLabel = (text: string) => (
    <Box mt={2} mb={1}>
      <Typography variant="subtitle2">{text}</Typography>
    </Box>
  );

  renderForm = () => {
    const branding = this.state.organizationBranding;

    return (
      <ValidatorForm onSubmit={this.saveBranding} instantValidate={true}>
        <Box p={3} bgcolor="background.paper" borderRadius={12} border={1} borderColor="divider">
          {this.renderLabel(
            this.translatorService.Tranlate('ORGANIZATION_BRANDING_TITLE_LABEL', 'Titlu document')
          )}
          <TextValidator
            fullWidth
            variant="outlined"
            name="title"
            value={branding.title}
            onChange={this.handleFieldChange('title')}
            placeholder={this.translatorService.Tranlate(
              'ORGANIZATION_BRANDING_TITLE_PLACEHOLDER',
              'Ex. Deviz estimativ de reparație'
            )}
          />

          {this.renderLabel(
            this.translatorService.Tranlate('ORGANIZATION_BRANDING_HEADER_LABEL', 'Detalii antet')
          )}
          <TextValidator
            fullWidth
            variant="outlined"
            name="headerDetails"
            multiline
            rows={3}
            value={branding.headerDetails}
            onChange={this.handleFieldChange('headerDetails')}
            placeholder={this.translatorService.Tranlate(
              'ORGANIZATION_BRANDING_HEADER_PLACEHOLDER',
              'Ex. Denumire companie S.A. · CIF ...'
            )}
          />

          {this.renderLabel(
            this.translatorService.Tranlate('ORGANIZATION_BRANDING_LOGO_LABEL', 'Logo companie')
          )}
          <Box display="flex" alignItems="center" mt={1}>
            <Box
              width={72}
              height={72}
              mr={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
              border={1}
              borderColor="divider"
              borderRadius={8}
              bgcolor="grey.100"
              style={{ overflow: 'hidden', flexShrink: 0, cursor: 'pointer' }}
              onClick={this.triggerLogoUpload}
            >
              {this.state.logoDataUrl ? (
                <img
                  src={this.state.logoDataUrl}
                  alt="logo"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  onError={() => this.setState({ logoDataUrl: null })}
                />
              ) : (
                <CloudUploadOutlinedIcon color="disabled" />
              )}
            </Box>
            <Button variant="outlined" color="primary" onClick={this.triggerLogoUpload}>
              {this.translatorService.Tranlate('ORGANIZATION_BRANDING_LOGO_REPLACE', 'Înlocuiește')}
            </Button>
            <input
              ref={this.fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={this.handleLogoSelected}
            />
          </Box>
          <Box mt={1}>
            <Typography variant="caption" display="block">
              {this.translatorService.Tranlate(
                'ORGANIZATION_BRANDING_LOGO_INFO',
                'PNG/JPG, maxim 2 MB, dimensiune recomandată ~300×100 px.'
              )}
            </Typography>
          </Box>

          {this.renderLabel(
            this.translatorService.Tranlate('ORGANIZATION_BRANDING_FOOTER_LABEL', 'Detalii subsol')
          )}
          <TextValidator
            fullWidth
            variant="outlined"
            name="footerDetails"
            multiline
            rows={2}
            value={branding.footerDetails}
            onChange={this.handleFieldChange('footerDetails')}
            placeholder={this.translatorService.Tranlate(
              'ORGANIZATION_BRANDING_FOOTER_PLACEHOLDER',
              'Ex. Document generat automat ...'
            )}
          />

          {this.renderLabel(
            this.translatorService.Tranlate('ORGANIZATION_BRANDING_NOTES_LABEL', 'Note adiționale')
          )}
          <TextValidator
            fullWidth
            variant="outlined"
            name="additionalNotes"
            multiline
            rows={3}
            value={branding.additionalNotes}
            onChange={this.handleFieldChange('additionalNotes')}
            placeholder={this.translatorService.Tranlate(
              'ORGANIZATION_BRANDING_NOTES_PLACEHOLDER',
              'Ex. clauze de valabilitate a ofertei, termeni și condiții...'
            )}
          />

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={this.state.isSaving}
            >
              {this.translatorService.Tranlate('SAVE', 'Salvează')}
            </Button>
          </Box>
        </Box>
      </ValidatorForm>
    );
  };

  renderPreview = () => {
    const branding = this.state.organizationBranding;
    const currency = this.translatorService.Tranlate(
      'ORGANIZATION_BRANDING_PREVIEW_CURRENCY',
      'lei'
    );
    const total = BRANDING_PREVIEW_PARTS.reduce((sum, item) => sum + (item.originalPrice ?? 0), 0);

    return (
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" className="font-weight-bold">
            {this.translatorService.Tranlate(
              'ORGANIZATION_BRANDING_PREVIEW_TITLE',
              'Previzualizare live'
            )}
          </Typography>
          <Button variant="outlined" color="primary" onClick={this.downloadModelPdf}>
            {this.translatorService.Tranlate(
              'ORGANIZATION_BRANDING_DOWNLOAD_TEST_PDF',
              'Descarcă PDF de test'
            )}
          </Button>
        </Box>

        <div ref={this.previewRef}>
          <Card style={{ width: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="flex-start">
                <Box
                  width={48}
                  height={48}
                  mr={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  border={1}
                  borderColor="divider"
                  borderRadius={8}
                  bgcolor="grey.100"
                  style={{ overflow: 'hidden', flexShrink: 0 }}
                >
                  {this.state.logoDataUrl ? (
                    <img
                      src={this.state.logoDataUrl}
                      alt="logo"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      onError={() => this.setState({ logoDataUrl: null })}
                    />
                  ) : null}
                </Box>
                <Typography variant="body2" style={{ whiteSpace: 'pre-line', paddingTop: 4 }}>
                  {branding.headerDetails}
                </Typography>
              </Box>

              <Box mt={2} mb={3} bgcolor="primary.main" style={{ height: 2, borderRadius: 2 }} />

              <Typography variant="h6" gutterBottom className="font-weight-bold">
                {branding.title}
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {this.translatorService.Tranlate('ORGANIZATION_BRANDING_PREVIEW_PART', 'Piesă')}
                    </TableCell>
                    <TableCell>
                      {this.translatorService.Tranlate(
                        'ORGANIZATION_BRANDING_PREVIEW_OPERATION',
                        'Operație'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {this.translatorService.Tranlate('ORGANIZATION_BRANDING_PREVIEW_PRICE', 'Preț')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {BRANDING_PREVIEW_PARTS.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.repairMethod}</TableCell>
                      <TableCell align="right">
                        {item.originalPrice} {currency}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box mt={2} textAlign="right">
                <Typography variant="subtitle1" className="font-weight-bold">
                  {this.translatorService.Tranlate('ORGANIZATION_BRANDING_PREVIEW_TOTAL', 'Total')}:{' '}
                  {total} {currency}
                </Typography>
              </Box>

              {branding.footerDetails ? (
                <Fragment>
                  <Box my={2}>
                    <Divider />
                  </Box>
                  <Typography variant="caption" style={{ whiteSpace: 'pre-line' }}>
                    {branding.footerDetails}
                  </Typography>
                </Fragment>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </Box>
    );
  };

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.organizationService = (this.context as AppContext).organizationService;

    return (
      <Fragment>
        <Box mt={1} pt={1}>
          <div className="d-flex flex-row text-center flex-wrap justify-content-center">
            <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
          </div>
          {!this.state.isLoading ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                {this.renderForm()}
              </Grid>
              <Grid item xs={12} md={5}>
                {this.renderPreview()}
              </Grid>
            </Grid>
          ) : null}
        </Box>
      </Fragment>
    );
  }
}

export default connect(
  (state: ApplicationState) => ({ appState: state.app }),
  null
)(withSnackbar(OrganizationBrandingDocument as any));
